import { SCORE_BANDS, SCORE_MAX, SCORE_MIN, type ScoreBand } from '@cyberscore/contracts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useId } from 'react';

/** The prototype's charts, drawn from real scores: the score gauge, rings, risk capsules and a line. */

export const BAND_COLOR: Record<ScoreBand, string> = {
  POOR: 'var(--band-poor)',
  FAIR: 'var(--band-fair)',
  GOOD: 'var(--band-good)',
  VERY_GOOD: 'var(--band-vgood)',
  EXCELLENT: 'var(--band-exc)',
};

const P = (cx: number, cy: number, r: number, deg: number) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
};
function arc(cx: number, cy: number, r: number, a1: number, a2: number) {
  const [x1, y1] = P(cx, cy, r, a1);
  const [x2, y2] = P(cx, cy, r, a2);
  return `M${x1.toFixed(2)} ${y1.toFixed(2)} A${r} ${r} 0 ${a2 - a1 > 180 ? 1 : 0} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

/** A credit-score style gauge over 300–1000 across 240°, with an optional dashed marker for a target. */
export function Gauge({ score, band, target }: { score: number; band: ScoreBand; target?: number | null }) {
  const cx = 160, cy = 160, r = 126, A0 = 150, SW = 240; // prettier-ignore
  const t = (s: number) => A0 + ((Math.max(SCORE_MIN, Math.min(SCORE_MAX, s)) - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * SW; // prettier-ignore
  const ghost = target && target > score ? target : null;
  const [mx, my] = P(cx, cy, r, t(score));
  const [gx, gy] = ghost ? P(cx, cy, r, t(ghost)) : [0, 0];
  // Band edges, leaving out any the target's label would sit on.
  const ticks = [300, 550, 650, 750, 850, 1000].filter((v) => !ghost || Math.abs(v - ghost) > 30);
  return (
    <svg viewBox="0 0 320 262" role="img" aria-label={`CyberScore ${score} of 1000`}>
      {SCORE_BANDS.map((b) => {
        const a1 = t(b.min) + 1.2;
        const a2 = t(Math.min(b.max + 1, SCORE_MAX)) - 1.2;
        const end = Math.min(a2, t(score));
        return (
          <g key={b.key}>
            <path d={arc(cx, cy, r, a1, a2)} stroke={BAND_COLOR[b.key]} strokeOpacity={0.18} strokeWidth={16} fill="none" strokeLinecap="round" />
            {end > a1 && <path d={arc(cx, cy, r, a1, end)} stroke={BAND_COLOR[b.key]} strokeWidth={16} fill="none" strokeLinecap="round" />}
          </g>
        );
      })}
      {ticks.map((v) => {
        const [x, y] = P(cx, cy, r + 24, t(v));
        return (
          <text key={v} x={x} y={y + 4} textAnchor="middle" className="gauge-tick">
            {v}
          </text>
        );
      })}
      {ghost && (
        <>
          <circle cx={gx} cy={gy} r={9} fill="var(--surface)" stroke="var(--ok-strong)" strokeWidth={2.5} strokeDasharray="3 3" />
          <text x={gx} y={gy - 18} textAnchor="middle" className="gauge-ghost">
            {ghost}
          </text>
        </>
      )}
      <circle cx={mx} cy={my} r={13} fill="var(--surface)" stroke={BAND_COLOR[band]} strokeWidth={5} />
    </svg>
  );
} // prettier-ignore

/** A progress ring with the percentage in the middle. */
export function Ring({ value, color = 'var(--primary)', size = 88, stroke = 9 }: { value: number; color?: string; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="ring" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--sunken)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={`${(c * v) / 100} ${c}`} />
      </svg>
      <div className="v">{Math.round(v)}%</div>
    </div>
  );
} // prettier-ignore

/** Segmented capsules: a 0–100 value as n filled segments. */
export function Caps({ value, n = 16, tone = '', small = false }: { value: number; n?: number; tone?: string; small?: boolean }) {
  const filled = Math.round((Math.max(0, Math.min(100, value)) / 100) * n);
  return (
    <span className={`caps ${tone}${small ? ' sm' : ''}`} role="img" aria-label={`${Math.round(value)} out of 100`}>
      {Array.from({ length: n }, (_, i) => (
        <i key={i} className={i < filled ? 'f' : undefined} />
      ))}
    </span>
  );
} // prettier-ignore

/** The tone a 0–100 resilience is shown in. */
export const resTone = (v: number) => (v >= 75 ? 'ok' : v >= 62 ? '' : v >= 50 ? 'warn' : 'bad');

/** A score change as a small rising or falling chip. */
export function Delta({ n }: { n: number }) {
  if (n === 0) return <span className="delta">0</span>;
  return (
    <span className={`delta ${n > 0 ? 'up' : 'down'}`}>
      {n > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
      {n > 0 ? '+' : '−'}
      {Math.abs(n)}
    </span>
  );
}

export interface LinePoint {
  x: number;
  y: number;
  label: string;
}

/** The score over time: points at their real dates, a light fill, and a dashed line at a mark. */
export function LineChart({ points, mark, w = 640, h = 220 }: { points: LinePoint[]; mark?: { y: number; label: string }; w?: number; h?: number }) {
  const id = useId();
  const pl = 40, pr = 16, pt = 16, pb = 28; // prettier-ignore
  const ys = points.map((p) => p.y);
  // A round step that gives four or five grid lines over the scores.
  const range = Math.max(...ys) - Math.min(...ys) + 40;
  const step = [25, 50, 100, 200].find((s) => range / s <= 4) ?? 250;
  const yMin = Math.max(300, Math.floor((Math.min(...ys) - 20) / step) * step);
  const yMax = Math.min(1000, Math.max(yMin + 4 * step, Math.ceil((Math.max(...ys) + 20) / step) * step));
  const x0 = Math.min(...points.map((p) => p.x));
  const x1 = Math.max(...points.map((p) => p.x));
  const X = (x: number) => pl + ((x - x0) / (x1 - x0 || 1)) * (w - pl - pr);
  const Y = (y: number) => pt + (1 - (y - yMin) / (yMax - yMin || 1)) * (h - pt - pb);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${X(p.x).toFixed(1)} ${Y(p.y).toFixed(1)}`).join(' ');
  const grid = Array.from({ length: Math.round((yMax - yMin) / step) + 1 }, (_, i) => yMin + step * i);
  // Date labels at most four, evenly picked.
  const picked = points.length <= 4 ? points : [0, 1, 2, 3].map((k) => points[Math.round((k * (points.length - 1)) / 3)]!);
  const labels = picked.filter((p, i) => picked.findIndex((q) => q.label === p.label) === i);
  const last = points[points.length - 1]!;
  return (
    <svg className="chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`Score history, now ${last.y}`}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--primary)" stopOpacity={0.22} />
          <stop offset="1" stopColor="var(--primary)" stopOpacity={0} />
        </linearGradient>
      </defs>
      {grid.map((v) => (
        <g key={v}>
          <line className="grid-l" x1={pl} x2={w - pr} y1={Y(v)} y2={Y(v)} />
          <text x={pl - 8} y={Y(v) + 4} textAnchor="end">{Math.round(v)}</text>
        </g>
      ))}
      {mark && mark.y > yMin && mark.y < yMax && (
        <>
          <line x1={pl} x2={w - pr} y1={Y(mark.y)} y2={Y(mark.y)} stroke="var(--ok)" strokeDasharray="4 4" />
          <text x={w - pr} y={Y(mark.y) - 6} textAnchor="end" style={{ fill: 'var(--ok)' }}>{mark.label}</text>
        </>
      )}
      {points.length > 1 && <path d={`${line} L${X(x1)} ${h - pb} L${X(x0)} ${h - pb} Z`} fill={`url(#${id})`} />}
      <path d={line} fill="none" stroke="var(--primary)" strokeWidth={2.5} strokeLinejoin="round" />
      {points.map((p, i) =>
        i === points.length - 1 ? (
          <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={6} fill="var(--primary)" stroke="var(--surface)" strokeWidth={3} />
        ) : (
          <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={3.5} fill="var(--surface)" stroke="var(--primary)" strokeWidth={2}>
            <title>{`${p.label}: ${p.y}`}</title>
          </circle>
        ),
      )}
      {labels.map((p, i) => (
        <text key={i} x={X(p.x)} y={h - 8} textAnchor="middle">{p.label}</text>
      ))}
    </svg>
  );
} // prettier-ignore
